import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar: React.FC = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    const getLinkClass = (path: string) => {
        const isActive = location.pathname === path ||
                        (path === '/' && location.pathname === '/home');
        return `nav-link w-nav-link ${isActive ? 'w--current' : ''}`;
    };

    const navigate = useNavigate();

    const handleNavClick = () => {
        setIsOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleGetStartedClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsOpen(false);
        if (location.pathname === '/sproutly') {
            document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/sproutly');
            setTimeout(() => {
                document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [location.pathname, navigate]);

    const isHome = location.pathname === '/' || location.pathname === '/home';

    const homeNavStyle: React.CSSProperties | undefined = isHome
        ? { backgroundColor: 'transparent' }
        : undefined;

    // When the mobile menu is open the menu background becomes white,
    // so keep link text dark even on the home page — otherwise links go invisible.
    const useWhiteLinks = isHome && !isOpen;

    const homeLinkStyle: React.CSSProperties | undefined = useWhiteLinks
        ? { color: 'rgba(255,255,255,0.9)' }
        : undefined;

    const homeActiveLinkStyle: React.CSSProperties | undefined = useWhiteLinks
        ? { color: '#ffffff', borderBottomColor: '#ffffff' }
        : undefined;

    const getLinkStyle = (path: string) => {
        if (!useWhiteLinks) return undefined;
        const isActive = location.pathname === path ||
                        (path === '/' && location.pathname === '/home');
        return isActive ? homeActiveLinkStyle : homeLinkStyle;
    };

    return (
        <div data-animation="default" data-collapse="medium" data-duration="400" data-easing="ease" data-easing2="ease" role="banner" className="navigation w-nav" style={homeNavStyle}>
            <div className="container">
                <div className="navigation-left-content">
                    <Link to="/" className="navigation-brand-logo-wrapper w-nav-brand" aria-label="home" onClick={handleNavClick} style={{ display: 'flex', alignItems: 'center' }}>
                        <h1
                            className="text-2xl font-heading font-bold"
                            aria-label={t('navbar.brandName') as string}
                            style={{
                                color: isHome ? '#ffffff' : '#0c1a40',
                                fontFamily: "Nunito, 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
                                display: 'inline-flex',
                                alignItems: 'center',
                                lineHeight: 1,
                            }}
                        >
                            <span>YagooGrow</span>
                        </h1>
                    </Link>
                </div>

                <div className="navigation-content">
                    <nav role="navigation" className={`navigation-menu w-nav-menu ${isOpen ? 'w--open' : ''}`}>
                        <Link to="/" className={getLinkClass('/')} onClick={handleNavClick} style={getLinkStyle('/')}>{t('navbar.home')}</Link>

                        <Link to="/sproutly" className={getLinkClass('/sproutly')} onClick={handleNavClick} style={getLinkStyle('/sproutly')}>
                            {t('navbar.sproutly')}
                        </Link>

                        <Link to="/blog" className={getLinkClass('/blog')} onClick={handleNavClick} style={getLinkStyle('/blog')}>{t('navbar.blog')}</Link>

                        <Link to="/support" className={getLinkClass('/support')} onClick={handleNavClick} style={getLinkStyle('/support')}>{t('navbar.contact')}</Link>

                        <a href="#download" className="button show-tablet w-inline-block" onClick={handleGetStartedClick}>
                            <div>{t('navbar.getStarted')}</div>
                        </a>
                    </nav>
                </div>

                <div className="navigation-left-content">
                    {/* On home, render an invisible spacer to preserve navbar height; on other pages render the real CTA */}
                    {isHome ? (
                        <span
                            className="button show-dekstop w-inline-block"
                            aria-hidden="true"
                            style={{ visibility: 'hidden', pointerEvents: 'none' }}
                        >
                            <div>{t('navbar.getStarted')}</div>
                        </span>
                    ) : (
                        <a
                            href="#download"
                            className="button show-dekstop w-inline-block"
                            onClick={handleGetStartedClick}
                        >
                            <div>{t('navbar.getStarted')}</div>
                        </a>
                    )}

                    <div
                        className={`nav-menu-button w-nav-button ${isOpen ? 'w--open' : ''}`}
                        style={{ WebkitUserSelect: 'text' }}
                        aria-label="menu"
                        role="button"
                        tabIndex={0}
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                    >
                        <div className="nav-menu-line-wrapper">
                            <div className="nav-menu-line top" style={isHome ? { backgroundColor: '#ffffff' } : undefined}></div>
                            <div className="nav-menu-line middle" style={isHome ? { backgroundColor: '#ffffff' } : undefined}></div>
                            <div className="nav-menu-line last" style={isHome ? { backgroundColor: '#ffffff' } : undefined}></div>
                        </div>
                    </div>
                </div>
            </div>

            {isOpen && <div className="w-nav-overlay" data-wf-ignore="" onClick={() => setIsOpen(false)}></div>}
        </div>
    );
};

export default Navbar;
